import React, { useState, useMemo } from "react";
import Modal from "@/components/controls/Modal";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import SingleSelect from "@/components/controls/SingleSelect";
import { UserData, UserStatus } from "@/entities/users/types";
import { useUsersInfinite, useUserStats } from "@/entities/users/hooks";
import { useTranslation } from "react-i18next";
import UserAvatar from "@/components/controls/UserAvatar";
import RecordTransferRow from "./userManagement/RecordTransferRow";
import { TransferData } from "./userManagement/hooks/types";

const TRANSLATION_KEY = "settings.userManagement.deactivateModal";

const ACTIVE_USER_FILTERS = { status: [UserStatus.ACTIVE] };

interface DeactivateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  userData: UserData;
  onDeactivate: (transferData: TransferData) => void;
}

const DeactivateUserModal: React.FC<DeactivateUserModalProps> = ({
  isOpen,
  onClose,
  userData,
  onDeactivate
}) => {
  const { t } = useTranslation();
  const { entities: users } = useUsersInfinite(ACTIVE_USER_FILTERS);
  const userStats = useUserStats(userData.id);
  const [transferData, setTransferData] = useState<TransferData>({
    companies: null,
    deals: null,
    leads: null,
    newReportingManager: null
  });

  const recordCounts = {
    companies: userStats.companies || 0,
    deals: userStats.deals || 0,
    leads: userStats.leads || 0,
    directReports: userData.directReports?.length || 0
  };

  const availableUsers = useMemo(() => {
    if (!users) return [];
    return users.map((user) => ({
      id: user.id,
      displayName: user.displayName,
      subtitle: user.jobTitle || user.departmentName || "",
      userData: user
    }));
  }, [users, userData.id]);

  const renderUserIcon = (userId: string | number): JSX.Element => {
    const user = users?.find((u) => u.id === userId);
    if (!user) {
      return <div className="size-6" />;
    }

    return (
      <UserAvatar
        userData={user}
        size="M"
        fallbackClassName="bg-gradient-to-t from-slate-600 to-tertiary text-xs"
      />
    );
  };

  const isFormValid = useMemo(() => {
    const hasActiveRecords =
      recordCounts.companies > 0 ||
      recordCounts.deals > 0 ||
      recordCounts.leads > 0;
    const hasDirectReports = recordCounts.directReports > 0;

    if (hasActiveRecords) {
      const recordTransfersValid =
        (recordCounts.companies === 0 || transferData.companies) &&
        (recordCounts.deals === 0 || transferData.deals) &&
        (recordCounts.leads === 0 || transferData.leads);

      if (!recordTransfersValid) return false;
    }

    if (hasDirectReports && !transferData.newReportingManager) {
      return false;
    }

    return true;
  }, [recordCounts, transferData]);

  const handleDeactivate = () => {
    if (isFormValid) {
      onDeactivate(transferData);
    }
  };

  const handleTransferChange = (field: keyof TransferData, value: string) => {
    setTransferData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const hasActiveRecords =
    recordCounts.companies > 0 ||
    recordCounts.deals > 0 ||
    recordCounts.leads > 0;
  const hasDirectReports = recordCounts.directReports > 0;

  return (
    <Modal
      open={isOpen}
      setIsOpen={(open) => !open && onClose()}
      title={
        <Text variant="title3H1">
          {t(`${TRANSLATION_KEY}.title`, { fullName: userData.displayName })}
        </Text>
      }
      content={
        <div className="flex flex-col gap-6 p-6">
          <Text variant="body">{t(`${TRANSLATION_KEY}.description`)}</Text>
          {hasActiveRecords && (
            <div className="flex flex-col gap-4">
              <Text variant="body">
                {t(`${TRANSLATION_KEY}.activeRecordsText`, {
                  firstName: userData.firstName
                })}
              </Text>
              <div className="flex flex-col gap-4">
                <RecordTransferRow
                  type="companies"
                  label={t(`${TRANSLATION_KEY}.companies`)}
                  count={recordCounts.companies}
                  selectedValue={transferData.companies}
                  onTransferChange={handleTransferChange}
                  t={t}
                  translationKey={TRANSLATION_KEY}
                />
                <RecordTransferRow
                  type="deals"
                  label={t(`${TRANSLATION_KEY}.deals`)}
                  count={recordCounts.deals}
                  selectedValue={transferData.deals}
                  onTransferChange={handleTransferChange}
                  t={t}
                  translationKey={TRANSLATION_KEY}
                />
                <RecordTransferRow
                  type="leads"
                  label={t(`${TRANSLATION_KEY}.leads`)}
                  count={recordCounts.leads}
                  selectedValue={transferData.leads}
                  onTransferChange={handleTransferChange}
                  t={t}
                  translationKey={TRANSLATION_KEY}
                />
              </div>
            </div>
          )}
          {hasDirectReports && (
            <div className="flex flex-col gap-4">
              <Text variant="body">
                {t(`${TRANSLATION_KEY}.directReportsText`, {
                  firstName: userData.firstName,
                  count: recordCounts.directReports
                })}
              </Text>
              <div className="flex items-center justify-between gap-4">
                <Text variant="body" className="min-w-0">
                  {t(`${TRANSLATION_KEY}.newReportingManager`)}
                </Text>
                <div className="w-[314px]">
                  <SingleSelect
                    fieldName="new-reporting-manager"
                    options={availableUsers}
                    selectedValue={transferData.newReportingManager || ""}
                    onSelection={(value) =>
                      handleTransferChange(
                        "newReportingManager",
                        value as string
                      )
                    }
                    placeholder={t(`${TRANSLATION_KEY}.select`)}
                    variant="form"
                    showSearch={true}
                    className="w-full"
                    renderIcon={renderUserIcon}
                    popoverClassName="w-[314px]"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      }
      footer={
        <div className="flex items-center justify-end gap-3">
          <Button variant="secondaryNoBg" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button
            destructive
            onClick={handleDeactivate}
            disabled={!isFormValid}
          >
            {t(`${TRANSLATION_KEY}.deactivate`)}
          </Button>
        </div>
      }
      contentClassname="max-w-2xl"
      hideDivider={true}
    />
  );
};

export default DeactivateUserModal;
